/**
 * KAN-944: клиента взял в работу другой менеджер (карточка закрыта сервером).
 * Вместо карточки: кто взял и "К списку". Несохранённый комментарий и
 * неотправленное сообщение (черновики клиента) показываются только для
 * чтения с кнопкой "Скопировать", чтобы передать их коллеге.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useMobileBack } from '@/hooks/useMobileBack';
import { clientCommentDraftKey, clientMessageDraftKey, readComposerDraft } from '@/lib/composerDraft';

const BTN = 'min-h-[48px] rounded-xl px-4 text-base font-medium';

interface UnsavedTextProps {
  title: string;
  text: string;
  copyLabel: string;
  testId: string;
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Скопировано');
  } catch {
    toast.error('Не удалось скопировать, выделите текст вручную');
  }
}

function UnsavedText({ title, text, copyLabel, testId }: UnsavedTextProps): JSX.Element {
  return (
    <div className="space-y-2 rounded-lg bg-gray-50 p-3">
      <p className="text-sm font-medium text-gray-700">{title}</p>
      <p className="whitespace-pre-wrap break-words text-base text-gray-900" data-testid={testId}>
        {text}
      </p>
      <button
        type="button"
        onClick={() => void copyText(text)}
        aria-label={copyLabel}
        className="min-h-[44px] rounded-xl border border-gray-300 bg-white px-4 text-sm font-medium text-gray-900"
      >
        Скопировать
      </button>
    </div>
  );
}

interface Props {
  clientId: string;
  takenBy: string;
}

export function MobileClientTakenByOther({ clientId, takenBy }: Props): JSX.Element {
  const back = useMobileBack();
  // Черновики читаются один раз: карточки уже нет, писать в них некому.
  const [comment] = useState(() => readComposerDraft(clientCommentDraftKey(clientId)));
  const [message] = useState(() => readComposerDraft(clientMessageDraftKey(clientId)));

  return (
    <section
      className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4"
      data-testid="mobile-client-taken"
      role="status"
    >
      <p className="text-base font-semibold text-amber-900">Клиента взял в работу {takenBy}</p>
      <p className="text-sm text-amber-900">Карточка больше недоступна. Если что-то не сохранилось, скопируйте и передайте коллеге.</p>
      {comment && (
        <UnsavedText
          title="Несохранённый комментарий"
          text={comment}
          copyLabel="Скопировать комментарий"
          testId="mobile-taken-comment"
        />
      )}
      {message && (
        <UnsavedText
          title="Неотправленное сообщение"
          text={message}
          copyLabel="Скопировать сообщение"
          testId="mobile-taken-message"
        />
      )}
      <button type="button" onClick={back} className={`${BTN} w-full bg-blue-600 text-white`}>
        К списку
      </button>
    </section>
  );
}
